import { useState, useEffect } from 'react';
import {
    Users as UsersIcon,
    Shield,
    UserCheck,
    Plus,
    Search,
    Edit2,
    Trash2,
    Eye,
    Crown,
    User,
    Mail,
    Phone,
    MapPin,
    Calendar,
    Lock,
    Unlock,
    Star,
    Settings, // Thêm icon Settings để dùng cho quản lý phân quyền
    MoreHorizontal
} from 'lucide-react';
import { usersApi } from '../services/api';
import { useNavigate } from 'react-router-dom'; // Thêm useNavigate để điều hướng
import { useAuth } from '../contexts/AuthContext';
import PageContainer from '../components/PageContainer';
import {
    ActionMenu,
    Button,
    DataToolbar,
    EmptyState,
    LocalPagination,
    LoadingState,
    Modal,
    ResponsiveDataView,
    SectionPanel,
    Input,
    Select,
    StatCard,
    StatusBadge,
    useLocalPagination,
    useToast,
} from '../components/ui';
import { openOnRowClick } from '../utils/rowClick';

const USERS_PAGE_SIZE = 10;

const Users = () => {
    const navigate = useNavigate(); // Thêm navigate hook
    const { user } = useAuth(); // Thêm auth context
    const { showToast: showUiToast } = useToast();
    const notify = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
        showUiToast(message, type);
    };
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRole, setSelectedRole] = useState('all');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [showForm, setShowForm] = useState(false);
    const [usersData, setUsersData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedUserDetail, setSelectedUserDetail] = useState<any>(null);
    const [selectedUserEdit, setSelectedUserEdit] = useState<any>(null);
    const [editForm, setEditForm] = useState<any>({});
    const [selectedUserChangePassword, setSelectedUserChangePassword] = useState<any>(null);
    const [changePasswordForm, setChangePasswordForm] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    const [selectedUserDelete, setSelectedUserDelete] = useState<any>(null);
    const [selectedUserToggleStatus, setSelectedUserToggleStatus] = useState<any>(null);
    const [openUserActionId, setOpenUserActionId] = useState<string | null>(null);

    // Fetch users function
    const fetchUsers = async () => {
        setLoading(true);
        try {
            const res = await usersApi.getAll();
            setUsersData(res.data.data || []);
        } catch (e) {
            setUsersData([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle delete user
    const handleDeleteUser = async () => {
        if (!selectedUserDelete) return;
        
        try {
            const response = await usersApi.delete(selectedUserDelete.id);
            // Use server message which includes deletion details
            notify(response.data.message || `Đã xóa vĩnh viễn ${selectedUserDelete.firstName} ${selectedUserDelete.lastName}!`, 'success');
            setSelectedUserDelete(null);
            fetchUsers(); // Refresh list
        } catch (error: any) {
            console.error('Delete user error:', error);
            notify(error.response?.data?.message || 'Có lỗi xảy ra khi xóa người dùng!', 'error');
        }
    };

    // Handle toggle user status
    const handleToggleUserStatus = async () => {
        if (!selectedUserToggleStatus) return;
        
        try {
            const response = await usersApi.toggleStatus(selectedUserToggleStatus.id);
            notify(response.data.message, 'success');
            setSelectedUserToggleStatus(null);
            fetchUsers(); // Refresh list
        } catch (error: any) {
            console.error('Toggle user status error:', error);
            notify(error.response?.data?.message || 'Có lỗi xảy ra khi thay đổi trạng thái người dùng!', 'error');
        }
    };

    // Add state for the user creation form
    const [createForm, setCreateForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: '',
        password: ''
    });
    const [isCreating, setIsCreating] = useState(false);

    useEffect(() => {
        fetchUsers();
    }, []);

    const roles = ['ADMIN', 'MANAGER', 'TEACHER', 'ASSISTANT', 'STAFF'];

    const filteredUsers = usersData.filter(user => {
        const matchesSearch = (user.firstName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (user.lastName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (user.email?.toLowerCase() || '').includes(searchTerm.toLowerCase());
        const matchesRole = selectedRole === 'all' || user.role === selectedRole;
        const matchesStatus = selectedStatus === 'all' ||
            (selectedStatus === 'active' && user.isActive) ||
            (selectedStatus === 'inactive' && !user.isActive);

        return matchesSearch && matchesRole && matchesStatus;
    });
    const {
        page: usersPage,
        pageSize: usersPageSize,
        pagedItems: pagedUsers,
        setPage: setUsersPage,
        setPageSize: setUsersPageSize,
    } = useLocalPagination(
        filteredUsers,
        USERS_PAGE_SIZE,
        `${searchTerm}|${selectedRole}|${selectedStatus}`,
    );

    const stats = {
        totalUsers: usersData.length,
        activeUsers: usersData.filter(u => u.isActive).length,
        admins: usersData.filter(u => u.role === 'ADMIN').length,
        teachers: usersData.filter(u => u.role === 'TEACHER').length,
        assistants: usersData.filter(u => u.role === 'ASSISTANT').length,
        managers: usersData.filter(u => u.role === 'MANAGER').length,
        staff: usersData.filter(u => u.role === 'STAFF').length
    };

    const getRoleIcon = (role: string) => {
        switch (role) {
            case 'ADMIN': return <Crown className="w-4 h-4" />;
            case 'MANAGER': return <Shield className="w-4 h-4" />;
            case 'TEACHER': return <UserCheck className="w-4 h-4" />;
            case 'ASSISTANT': return <User className="w-4 h-4" />;
            case 'STAFF': return <UsersIcon className="w-4 h-4" />;
            default: return <User className="w-4 h-4" />;
        }
    };

    const getRoleText = (role: string) => {
        switch (role) {
            case 'ADMIN': return 'Quản trị viên';
            case 'MANAGER': return 'Quản lý';
            case 'TEACHER': return 'Giáo viên';
            case 'ASSISTANT': return 'Trợ giảng';
            case 'STAFF': return 'Nhân viên';
            default: return role;
        }
    };

    const getRoleTone = (role: string): 'success' | 'warning' | 'danger' | 'neutral' | 'info' => {
        switch (role) {
            case 'ADMIN': return 'danger';
            case 'MANAGER': return 'info';
            case 'TEACHER': return 'info';
            case 'ASSISTANT': return 'success';
            case 'STAFF': return 'neutral';
            default: return 'neutral';
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('vi-VN');
    };

    const formatDateTime = (dateString: string) => {
        return new Date(dateString).toLocaleString('vi-VN');
    };

    const getInitials = (firstName: string, lastName: string) => {
        return `${firstName[0]}${lastName.split(' ')[0][0]}`;
    };

    const getUserFullName = (userItem: any) => `${userItem.firstName || ''} ${userItem.lastName || ''}`.trim() || 'chưa có tên';

    const handleEditUser = (userItem: any) => {
        setSelectedUserEdit(userItem);
        setEditForm({ ...userItem });
    };

    const handleChangePassword = (userItem: any) => {
        setSelectedUserChangePassword(userItem);
        setChangePasswordForm({ newPassword: '', confirmPassword: '' });
    };

    const handleCreateUser = async () => {
        setIsCreating(true);
        try {
            if (!createForm.firstName || !createForm.lastName || !createForm.email || !createForm.role || !createForm.password) {
                notify('Vui lòng điền đầy đủ thông tin!', 'error');
                setIsCreating(false);
                return;
            }
            if (!createForm.password || createForm.password.length < 6) {
                notify('Mật khẩu phải có ít nhất 6 ký tự!', 'error');
                setIsCreating(false);
                return;
            }
            await usersApi.create({
                firstName: createForm.firstName,
                lastName: createForm.lastName,
                email: createForm.email,
                phone: createForm.phone,
                role: createForm.role,
                password: createForm.password
            });
            notify('✅ Tạo người dùng thành công!', 'success');
            setShowForm(false);
            setCreateForm({ firstName: '', lastName: '', email: '', phone: '', role: '', password: '' });
            const res = await usersApi.getAll();
            setUsersData(res.data.data || []);
        } catch (err: any) {
            notify(err?.response?.data?.message || '❌ Lỗi khi tạo người dùng!', 'error');
        } finally {
            setIsCreating(false);
        }
    };

    const closeChangePasswordModal = () => {
        setSelectedUserChangePassword(null);
        setChangePasswordForm({ newPassword: '', confirmPassword: '' });
    };

    const renderUserCustomMenu = (userItem: any) => {
        const userName = getUserFullName(userItem);
        const isOpen = openUserActionId === userItem.id;

        return (
            <ActionMenu
                open={isOpen}
                onOpenChange={(open) => setOpenUserActionId(open ? userItem.id : null)}
                triggerLabel={`Tuỳ chỉnh người dùng ${userName}`}
                triggerTitle="Tuỳ chỉnh"
                triggerIcon={<MoreHorizontal className="h-4 w-4" />}
            >
                        <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                                setOpenUserActionId(null);
                                handleEditUser(userItem);
                            }}
                            className="flex w-full items-center px-4 py-2 text-sm text-ink-700 transition-colors hover:bg-ink-100"
                        >
                            <Edit2 className="mr-3 h-4 w-4" />
                            Chỉnh sửa
                        </button>
                        {user?.role === 'ADMIN' && (
                    <>
                        <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                                setOpenUserActionId(null);
                                handleChangePassword(userItem);
                            }}
                            className="flex w-full items-center px-4 py-2 text-sm text-ink-700 transition-colors hover:bg-ink-100"
                        >
                            <Lock className="mr-3 h-4 w-4" />
                            Đổi mật khẩu
                        </button>
                        <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                                setOpenUserActionId(null);
                                navigate(`/users/${userItem.id}/permissions`);
                            }}
                            className="flex w-full items-center px-4 py-2 text-sm text-ink-700 transition-colors hover:bg-ink-100"
                        >
                            <Settings className="mr-3 h-4 w-4" />
                            Quản lý phân quyền
                        </button>
                    </>
                        )}
                        <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                                setOpenUserActionId(null);
                                setSelectedUserToggleStatus(userItem);
                            }}
                            className="flex w-full items-center px-4 py-2 text-sm text-ink-700 transition-colors hover:bg-ink-100"
                        >
                            {userItem.isActive ? <Lock className="mr-3 h-4 w-4" /> : <Unlock className="mr-3 h-4 w-4" />}
                            {userItem.isActive ? 'Khóa tài khoản' : 'Mở khóa tài khoản'}
                        </button>
                        <div className="my-1 border-t border-ink-200" />
                        <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                                setOpenUserActionId(null);
                                setSelectedUserDelete(userItem);
                            }}
                            className="flex w-full items-center px-4 py-2 text-sm text-danger-600 transition-colors hover:bg-danger-50"
                        >
                            <Trash2 className="mr-3 h-4 w-4" />
                            Xóa
                        </button>
            </ActionMenu>
        );
    };

    const renderUsersTable = () => (
        <table className="min-w-full divide-y divide-ink-200">
            <thead>
                <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-ink-500">Người dùng</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-ink-500">Vai trò</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-ink-500">Trạng thái</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-ink-500">Số điện thoại</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-ink-500">Ngày tham gia</th>
                    <th scope="col" className="w-12 px-3 py-3"><span className="sr-only">Tuỳ chỉnh</span></th>
                </tr>
            </thead>
            <tbody className="divide-y divide-ink-200 bg-white">
                {pagedUsers.map((userItem) => (
                    <tr key={userItem.id} onClick={openOnRowClick(() => setSelectedUserDetail(userItem))} className="cursor-pointer transition-colors hover:bg-ink-50">
                        <td className="min-w-[240px] px-4 py-4">
                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ink-100 text-sm font-semibold text-ink-700">
                                    {getInitials(userItem.firstName, userItem.lastName)}
                                </div>
                                <div className="min-w-0">
                                    <button
                                        type="button"
                                        onClick={() => setSelectedUserDetail(userItem)}
                                        aria-label={`Xem chi tiết người dùng ${getUserFullName(userItem)}`}
                                        className="block max-w-full cursor-pointer truncate text-left text-sm font-medium text-ink-900 hover:text-academy-600"
                                    >
                                        {getUserFullName(userItem)}
                                    </button>
                                    <p className="truncate text-xs text-ink-500">{userItem.email}</p>
                                </div>
                            </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-4">
                            <StatusBadge tone={getRoleTone(userItem.role)} icon={getRoleIcon(userItem.role)}>
                                {getRoleText(userItem.role)}
                            </StatusBadge>
                        </td>
                        <td className="whitespace-nowrap px-4 py-4">
                            <StatusBadge tone={userItem.isActive ? 'success' : 'neutral'}>
                                {userItem.isActive ? 'Hoạt động' : 'Không hoạt động'}
                            </StatusBadge>
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 text-sm text-ink-600">{userItem.phone || '---'}</td>
                        <td className="whitespace-nowrap px-4 py-4 text-sm text-ink-600">{formatDate(userItem.createdAt)}</td>
                        <td className="px-3 py-4 text-center">{renderUserCustomMenu(userItem)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    const renderUsersCards = () => (
        <div className="grid gap-3">
            {pagedUsers.map((userItem) => (
                <article key={userItem.id} onClick={openOnRowClick(() => setSelectedUserDetail(userItem))} className="cursor-pointer rounded-lg border border-ink-200 bg-white p-4 transition-colors hover:bg-ink-50">
                    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
                        <div className="flex min-w-0 items-start gap-3">
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ink-100 text-sm font-semibold text-ink-700">
                                {getInitials(userItem.firstName, userItem.lastName)}
                            </div>
                            <div className="min-w-0 flex-1">
                                <h3 className="truncate text-sm font-semibold text-ink-900">
                                    <button
                                        type="button"
                                        onClick={() => setSelectedUserDetail(userItem)}
                                        aria-label={`Xem chi tiết người dùng ${getUserFullName(userItem)}`}
                                        className="max-w-full cursor-pointer truncate text-left"
                                    >
                                        {getUserFullName(userItem)}
                                    </button>
                                </h3>
                                <p className="truncate text-xs text-ink-500">{userItem.email}</p>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    <StatusBadge tone={getRoleTone(userItem.role)} icon={getRoleIcon(userItem.role)}>
                                        {getRoleText(userItem.role)}
                                    </StatusBadge>
                                    <StatusBadge tone={userItem.isActive ? 'success' : 'neutral'}>
                                        {userItem.isActive ? 'Hoạt động' : 'Không hoạt động'}
                                    </StatusBadge>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end">
                            {renderUserCustomMenu(userItem)}
                        </div>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-3 text-sm text-ink-600">
                        <div>
                            <span className="block text-xs font-medium text-ink-400">Số điện thoại</span>
                            {userItem.phone || '---'}
                        </div>
                        <div>
                            <span className="block text-xs font-medium text-ink-400">Ngày tham gia</span>
                            {formatDate(userItem.createdAt)}
                        </div>
                    </div>
                </article>
            ))}
        </div>
    );

    return (
        <PageContainer
            title="Tài khoản hệ thống"
            subtitle={`${usersData.length} tài khoản · vai trò và quyền của từng người.`}
            actions={user?.role === 'ADMIN' ? (
                <Button icon={<Plus className="h-4 w-4" />} onClick={() => setShowForm(true)}>
                    Thêm người dùng
                </Button>
            ) : null}
        >

            {/* Stats Cards */}
            <div className="grid gap-px overflow-hidden rounded-lg border border-ink-200 bg-ink-200 grid-cols-2 xl:grid-cols-4">
                <StatCard label="Tổng người dùng" value={stats.totalUsers} detail="Tài khoản hệ thống" icon={<UsersIcon className="h-5 w-5" />} tone="academy" />
                <StatCard label="Đang hoạt động" value={stats.activeUsers} detail={`${stats.totalUsers > 0 ? ((stats.activeUsers / stats.totalUsers) * 100).toFixed(1) : 0}%`} icon={<UserCheck className="h-5 w-5" />} tone="action" />
                <StatCard label="Giáo viên" value={stats.teachers} detail={`Chính + ${stats.assistants} trợ giảng`} icon={<User className="h-5 w-5" />} tone="academy" />
                <StatCard label="Quản trị" value={stats.admins + stats.managers} detail={`${stats.admins} Admin + ${stats.managers} Manager`} icon={<Shield className="h-5 w-5" />} tone="warning" />
            </div>

            {/* Filters */}
            <DataToolbar
                className="sm:items-center"
                controlsLayoutClassName="grid flex-1 gap-2 sm:grid-cols-3 lg:flex lg:flex-wrap lg:items-center"
            >
                <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Tìm tên hoặc email…"
                    leftIcon={<Search className="h-4 w-4" />}
                    aria-label="Tìm kiếm người dùng"
                    className="min-h-9 lg:w-72"
                />
                <select
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    aria-label="Lọc theo vai trò"
                    className="min-h-9 w-full rounded-lg border border-ink-300 bg-white px-3 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100 lg:w-44"
                >
                    <option value="all">Mọi vai trò</option>
                    {roles.map(role => (
                        <option key={role} value={role}>{getRoleText(role)}</option>
                    ))}
                </select>
                <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    aria-label="Lọc theo trạng thái"
                    className="min-h-9 w-full rounded-lg border border-ink-300 bg-white px-3 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100 lg:w-44"
                >
                    <option value="all">Mọi trạng thái</option>
                    <option value="active">Hoạt động</option>
                    <option value="inactive">Không hoạt động</option>
                </select>
            </DataToolbar>

            {/* Users Table */}
            <SectionPanel className="overflow-hidden p-0 max-lg:rounded-none max-lg:border-0 max-lg:bg-transparent max-lg:shadow-none">
                {loading ? (
                    <div className="p-6"><LoadingState title="Đang tải dữ liệu người dùng..." /></div>
                ) : filteredUsers.length === 0 ? (
                    <div className="p-6">
                        <EmptyState title="Không tìm thấy người dùng" description="Thử thay đổi bộ lọc hoặc tạo người dùng mới." />
                        <div className="pb-4 text-center">
                        <Button
                            onClick={() => setShowForm(true)}
                        >
                            Thêm người dùng mới
                        </Button>
                        </div>
                    </div>
                ) : (
                    <>
                    <ResponsiveDataView
                        desktopLabel="Danh sách người dùng"
                        mobileLabel="Thẻ người dùng"
                        desktop={renderUsersTable()}
                        mobile={renderUsersCards()}
                    />
                    <LocalPagination
                        label="danh sách người dùng"
                        page={usersPage}
                        pageSize={usersPageSize}
                        totalItems={filteredUsers.length}
                        pageSizeOptions={[10, 20, 50]}
                        onPageChange={setUsersPage}
                        onPageSizeChange={setUsersPageSize}
                    />
                    </>
                )}
            </SectionPanel>

            <Modal
                open={showForm}
                title="Thêm người dùng mới"
                onClose={() => setShowForm(false)}
                className="sm:max-w-2xl"
                footer={(
                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <Button variant="secondary" onClick={() => setShowForm(false)}>
                            Hủy
                        </Button>
                        <Button onClick={handleCreateUser} loading={isCreating}>
                            {isCreating ? 'Đang tạo...' : 'Tạo Người dùng'}
                        </Button>
                    </div>
                )}
            >
                <div className="space-y-5">
                    <div className="grid gap-4 md:grid-cols-2">
                        <Input
                            label="Họ *"
                            placeholder="Nguyễn"
                            value={createForm.firstName}
                            onChange={e => setCreateForm(f => ({ ...f, firstName: e.target.value }))}
                        />
                        <Input
                            label="Tên *"
                            placeholder="Văn A"
                            value={createForm.lastName}
                            onChange={e => setCreateForm(f => ({ ...f, lastName: e.target.value }))}
                        />
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                        <Input
                            label="Email *"
                            type="email"
                            placeholder="user@athena.edu"
                            value={createForm.email}
                            onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))}
                        />
                        <Input
                            label="Số điện thoại"
                            type="tel"
                            placeholder="0901234567"
                            value={createForm.phone}
                            onChange={e => setCreateForm(f => ({ ...f, phone: e.target.value }))}
                        />
                    </div>

                    <Select
                        label="Vai trò *"
                        value={createForm.role}
                        onChange={e => setCreateForm(f => ({ ...f, role: e.target.value }))}
                    >
                        <option value="">Chọn vai trò</option>
                        {roles.map(role => (
                            <option key={role} value={role}>{getRoleText(role)}</option>
                        ))}
                    </Select>

                    <Input
                        label="Mật khẩu tạm thời *"
                        type="password"
                        placeholder="Mật khẩu tạm thời"
                        value={createForm.password}
                        onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))}
                    />
                </div>
            </Modal>

            {selectedUserDetail && (
                <Modal
                    open={Boolean(selectedUserDetail)}
                    title="Xem người dùng"
                    onClose={() => setSelectedUserDetail(null)}
                    className="sm:max-w-xl"
                    footer={(
                        <div className="flex justify-end">
                            <Button variant="secondary" onClick={() => setSelectedUserDetail(null)}>
                                Đóng
                            </Button>
                        </div>
                    )}
                >
                    <div className="space-y-5">
                        <div className="flex items-start gap-4">
                            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-ink-100 text-lg font-semibold text-ink-700">
                                {getInitials(selectedUserDetail.firstName, selectedUserDetail.lastName)}
                            </div>
                            <div className="min-w-0 flex-1">
                                <h4 className="text-lg font-semibold text-ink-900">{selectedUserDetail.firstName} {selectedUserDetail.lastName}</h4>
                                <p className="mt-1 truncate text-sm text-ink-500">{selectedUserDetail.email}</p>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    <StatusBadge tone={getRoleTone(selectedUserDetail.role)} icon={getRoleIcon(selectedUserDetail.role)}>
                                        {getRoleText(selectedUserDetail.role)}
                                    </StatusBadge>
                                    <StatusBadge tone={selectedUserDetail.isActive ? 'success' : 'neutral'}>
                                        {selectedUserDetail.isActive ? 'Hoạt động' : 'Không hoạt động'}
                                    </StatusBadge>
                                </div>
                            </div>
                        </div>

                        <div className="grid gap-3 sm:grid-cols-2">
                            <div className="rounded-lg border border-ink-200 p-3">
                                <div className="flex items-center gap-2 text-xs font-medium text-ink-400">
                                    <Mail className="h-4 w-4" />
                                    Email
                                </div>
                                <p className="mt-1 break-words text-sm text-ink-800">{selectedUserDetail.email || '---'}</p>
                            </div>
                            <div className="rounded-lg border border-ink-200 p-3">
                                <div className="flex items-center gap-2 text-xs font-medium text-ink-400">
                                    <Phone className="h-4 w-4" />
                                    Số điện thoại
                                </div>
                                <p className="mt-1 text-sm text-ink-800">{selectedUserDetail.phone || '---'}</p>
                            </div>
                            <div className="rounded-lg border border-ink-200 p-3">
                                <div className="flex items-center gap-2 text-xs font-medium text-ink-400">
                                    <MapPin className="h-4 w-4" />
                                    Cơ sở
                                </div>
                                <p className="mt-1 text-sm text-ink-800">{selectedUserDetail.campus || '---'}</p>
                            </div>
                            <div className="rounded-lg border border-ink-200 p-3">
                                <div className="flex items-center gap-2 text-xs font-medium text-ink-400">
                                    <Calendar className="h-4 w-4" />
                                    Ngày tham gia
                                </div>
                                <p className="mt-1 text-sm text-ink-800">{formatDate(selectedUserDetail.createdAt)}</p>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {selectedUserEdit && (
                <Modal
                    open={Boolean(selectedUserEdit)}
                    title="Chỉnh sửa người dùng"
                    onClose={() => setSelectedUserEdit(null)}
                    className="sm:max-w-xl"
                    footer={(
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <Button variant="secondary" onClick={() => setSelectedUserEdit(null)}>
                                Hủy
                            </Button>
                            <Button type="submit" form="user-edit-form">
                                Lưu
                            </Button>
                        </div>
                    )}
                >
                        <form
                            id="user-edit-form"
                            onSubmit={async e => {
                                e.preventDefault();
                                try {
                                    await usersApi.update(selectedUserEdit.id, editForm);
                                    setSelectedUserEdit(null);
                                    // Refresh user list
                                    const res = await usersApi.getAll();
                                    setUsersData(res.data.data || []);
                                    notify('✅ Cập nhật người dùng thành công!', 'success');
                                } catch (err) {
                                    notify('❌ Lỗi khi cập nhật người dùng!', 'error');
                                }
                            }}
                            className="space-y-5"
                        >
                            <div className="flex items-center gap-4">
                                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-ink-100 text-lg font-semibold text-ink-700">
                                    {getInitials(editForm.firstName, editForm.lastName)}
                                </div>
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold text-ink-900">{getUserFullName(editForm)}</p>
                                    <p className="truncate text-sm text-ink-500">{editForm.email || 'Chưa có email'}</p>
                                </div>
                            </div>
                            <div className="grid gap-4 sm:grid-cols-2">
                                <Input
                                    label="Họ"
                                    value={editForm.firstName}
                                    onChange={e => setEditForm((f: any) => ({ ...f, firstName: e.target.value }))}
                                    placeholder="Họ"
                                    required
                                />
                                <Input
                                    label="Tên"
                                    value={editForm.lastName}
                                    onChange={e => setEditForm((f: any) => ({ ...f, lastName: e.target.value }))}
                                    placeholder="Tên"
                                    required
                                />
                                <Input
                                    label="Email"
                                    type="email"
                                    value={editForm.email}
                                    onChange={e => setEditForm((f: any) => ({ ...f, email: e.target.value }))}
                                    placeholder="Email"
                                    required
                                />
                                <Input
                                    label="Số điện thoại"
                                    value={editForm.phone}
                                    onChange={e => setEditForm((f: any) => ({ ...f, phone: e.target.value }))}
                                    placeholder="Số điện thoại"
                                />
                                <Select
                                    label="Vai trò"
                                    value={editForm.role}
                                    onChange={e => setEditForm((f: any) => ({ ...f, role: e.target.value }))}
                                >
                                    {roles.map(role => (
                                        <option key={role} value={role}>{getRoleText(role)}</option>
                                    ))}
                                </Select>
                                <Select
                                    label="Trạng thái"
                                    value={editForm.isActive ? 'active' : 'inactive'}
                                    onChange={e => setEditForm((f: any) => ({ ...f, isActive: e.target.value === 'active' }))}
                                >
                                    <option value="active">Hoạt động</option>
                                    <option value="inactive">Không hoạt động</option>
                                </Select>
                            </div>
                        </form>
                </Modal>
            )}

            {selectedUserChangePassword && (
                <Modal
                    open={Boolean(selectedUserChangePassword)}
                    title={`Đổi mật khẩu cho ${selectedUserChangePassword.firstName} ${selectedUserChangePassword.lastName}`}
                    onClose={closeChangePasswordModal}
                    className="sm:max-w-md"
                    footer={(
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <Button variant="secondary" onClick={closeChangePasswordModal}>
                                Hủy
                            </Button>
                            <Button type="submit" form="user-password-form" className="border-warning-600 bg-warning-600 hover:border-warning-700 hover:bg-warning-700">
                                Đổi mật khẩu
                            </Button>
                        </div>
                    )}
                >
                        <form id="user-password-form" onSubmit={async (e) => {
                            e.preventDefault();
                            if (changePasswordForm.newPassword !== changePasswordForm.confirmPassword) {
                                notify('Mật khẩu xác nhận không khớp!', 'error');
                                return;
                            }
                            if (changePasswordForm.newPassword.length < 6) {
                                notify('Mật khẩu phải có ít nhất 6 ký tự!', 'error');
                                return;
                            }
                            
                            try {
                                await usersApi.changePassword(selectedUserChangePassword.id, changePasswordForm.newPassword);
                                notify(`Đã đổi mật khẩu thành công cho ${selectedUserChangePassword.firstName} ${selectedUserChangePassword.lastName}!`, 'success');
                                setSelectedUserChangePassword(null);
                                setChangePasswordForm({ newPassword: '', confirmPassword: '' });
                            } catch (error: any) {
                                console.error('Change password error:', error);
                                notify(error.response?.data?.message || 'Có lỗi xảy ra khi đổi mật khẩu!', 'error');
                            }
                        }}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-ink-700 mb-1">
                                        Mật khẩu mới
                                    </label>
                                    <input
                                        type="password"
                                        value={changePasswordForm.newPassword}
                                        onChange={(e) => setChangePasswordForm(f => ({ ...f, newPassword: e.target.value }))}
                                        className="min-h-10 w-full rounded-lg border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100"
                                        required
                                        minLength={6}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-ink-700 mb-1">
                                        Xác nhận mật khẩu
                                    </label>
                                    <input
                                        type="password"
                                        value={changePasswordForm.confirmPassword}
                                        onChange={(e) => setChangePasswordForm(f => ({ ...f, confirmPassword: e.target.value }))}
                                        className="min-h-10 w-full rounded-lg border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100"
                                        required
                                        minLength={6}
                                    />
                                </div>
                            </div>
                        </form>
                </Modal>
            )}

            {selectedUserDelete && (
                <Modal
                    open={Boolean(selectedUserDelete)}
                    title="Xóa người dùng"
                    description="Thao tác này không thể hoàn tác"
                    onClose={() => setSelectedUserDelete(null)}
                    className="sm:max-w-md"
                    footer={(
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <Button variant="secondary" onClick={() => setSelectedUserDelete(null)}>
                                Hủy
                            </Button>
                            <Button variant="danger" onClick={handleDeleteUser}>
                                Xóa
                            </Button>
                        </div>
                    )}
                >
                        <div className="flex items-center mb-4">
                            <div className="w-12 h-12 bg-danger-100 rounded-full flex items-center justify-center mr-4">
                                <Trash2 className="w-6 h-6 text-danger-600" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-ink-900">Xóa người dùng</h3>
                                <p className="text-sm text-ink-500">Thao tác này không thể hoàn tác</p>
                            </div>
                        </div>
                        <p className="text-ink-700 mb-6">
                            Bạn có chắc chắn muốn <strong className="text-danger-600">xóa vĩnh viễn</strong> <strong>{selectedUserDelete.firstName} {selectedUserDelete.lastName}</strong> ({selectedUserDelete.email})?
                            <br/>
                            <span className="text-sm text-ink-500 mt-2 block">
                                ⚠️ Hành động này sẽ xóa hoàn toàn tài khoản và tất cả dữ liệu liên quan (lớp học, điểm danh, phân quyền).
                            </span>
                        </p>
                </Modal>
            )}

            {selectedUserToggleStatus && (
                <Modal
                    open={Boolean(selectedUserToggleStatus)}
                    title={selectedUserToggleStatus.isActive ? 'Khóa tài khoản' : 'Mở khóa tài khoản'}
                    description="Thay đổi trạng thái người dùng"
                    onClose={() => setSelectedUserToggleStatus(null)}
                    className="sm:max-w-md"
                    footer={(
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <Button variant="secondary" onClick={() => setSelectedUserToggleStatus(null)}>
                                Hủy
                            </Button>
                            <Button
                                onClick={handleToggleUserStatus}
                                className={selectedUserToggleStatus.isActive
                                    ? 'border-warning-600 bg-warning-600 hover:border-warning-700 hover:bg-warning-700'
                                    : 'border-success-600 bg-success-600 hover:border-success-700 hover:bg-success-700'}
                            >
                                {selectedUserToggleStatus.isActive ? 'Khóa' : 'Mở khóa'}
                            </Button>
                        </div>
                    )}
                >
                        <div className="flex items-center mb-4">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 ${
                                selectedUserToggleStatus.isActive ? 'bg-warning-100' : 'bg-success-100'
                            }`}>
                                {selectedUserToggleStatus.isActive ? 
                                    <Lock className="w-6 h-6 text-warning-600" /> : 
                                    <Unlock className="w-6 h-6 text-success-600" />
                                }
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-ink-900">
                                    {selectedUserToggleStatus.isActive ? 'Khóa tài khoản' : 'Mở khóa tài khoản'}
                                </h3>
                                <p className="text-sm text-ink-500">Thay đổi trạng thái người dùng</p>
                            </div>
                        </div>
                        <p className="text-ink-700 mb-6">
                            Bạn có chắc chắn muốn <strong>{selectedUserToggleStatus.isActive ? 'khóa' : 'mở khóa'}</strong> tài khoản của <strong>{selectedUserToggleStatus.firstName} {selectedUserToggleStatus.lastName}</strong> ({selectedUserToggleStatus.email})?
                        </p>
                </Modal>
            )}

        </PageContainer>
    );
};

export default Users;
